import React, { Component } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import { Helmet } from 'react-helmet';

// Predefined families — must match seedDefinitions.js keys exactly.
// Admin picks from this list; Badge Key auto-fills with the same value.
const FAMILY_OPTIONS = [
  // General activity
  { value: 'checkins',        label: 'Check-ins' },
  { value: 'events',          label: 'Events (General)' },
  { value: 'countries',       label: 'Countries Visited' },
  // Venue types
  { value: 'cocktail_bar',    label: 'Cocktail Bar' },
  { value: 'nightclub',       label: 'Nightclub' },
  { value: 'beach_bar',       label: 'Beach Bar' },
  // Event types
  { value: 'after_work',      label: 'After Work' },
  { value: 'after_ski',       label: 'After Ski' },
  { value: 'beer_tasting',    label: 'Beer Tasting' },
  { value: 'brunch',          label: 'Brunch' },
  { value: 'concert',         label: 'Concert' },
  { value: 'dish_of_the_day', label: 'Dish of the Day' },
  { value: 'dj',              label: 'DJ' },
  { value: 'drink_tasting',   label: 'Drink Tasting' },
  { value: 'festival',        label: 'Festival' },
  { value: 'happy_hour',      label: 'Happy Hour' },
  { value: 'karaoke',         label: 'Karaoke' },
  { value: 'live_music',      label: 'Live Music' },
  { value: 'quiz',            label: 'Quiz' },
  { value: 'live_sport',      label: 'Live Sport' },
  { value: 'speed_dating',    label: 'Speed Dating' },
  { value: 'spirit_tasting',  label: 'Spirit Tasting' },
  { value: 'wine_tasting',    label: 'Wine Tasting' },
  { value: 'student_event',   label: 'Student Event' },
  { value: 'contest',         label: 'Contest' },
  // Special
  { value: 'founding_member', label: 'Founding Member' },
];

// ─── Raw counter-key strings that must exactly match what counters.js writes ──
const METRIC_OPTIONS = [
  { value: 'totalCheckins',                  label: 'Total Check-ins' },
  { value: 'uniqueVenues',                   label: 'Unique Venues' },
  { value: 'eventsAttended',                 label: 'Events Attended' },
  { value: 'countries',                      label: 'Countries Visited' },
  { value: 'byCategoryUnique.Cocktail bar',  label: 'Unique Cocktail Bars' },
  { value: 'byCategoryUnique.Discothèque',   label: 'Unique Nightclubs' },
  { value: 'byCategoryUnique.Beach bar',     label: 'Unique Beach Bars' },
  { value: 'byEventType.after_work',         label: 'Event: After Work' },
  { value: 'byEventType.after_ski',          label: 'Event: After Ski' },
  { value: 'byEventType.beer_tasting',       label: 'Event: Beer Tasting' },
  { value: 'byEventType.brunch',             label: 'Event: Brunch' },
  { value: 'byEventType.concert',            label: 'Event: Concert' },
  { value: 'byEventType.dish_of_the_day',    label: 'Event: Dish of the Day' },
  { value: 'byEventType.dj',                 label: 'Event: DJ' },
  { value: 'byEventType.drink_tasting',      label: 'Event: Drink Tasting' },
  { value: 'byEventType.festival',           label: 'Event: Festival' },
  { value: 'byEventType.happy_hour',         label: 'Event: Happy Hour' },
  { value: 'byEventType.karaoke',            label: 'Event: Karaoke' },
  { value: 'byEventType.live_music',         label: 'Event: Live Music' },
  { value: 'byEventType.quiz',               label: 'Event: Quiz' },
  { value: 'byEventType.live_sport',         label: 'Event: Live Sport' },
  { value: 'byEventType.speed_dating',       label: 'Event: Speed Dating' },
  { value: 'byEventType.spirit_tasting',     label: 'Event: Spirit Tasting' },
  { value: 'byEventType.wine_tasting',       label: 'Event: Wine Tasting' },
  { value: 'byEventType.student_event',      label: 'Event: Student Event' },
  { value: 'byEventType.contest',            label: 'Event: Contest' },
];

const RARITY_OPTIONS = ['COMMON', 'RARE', 'EPIC', 'LEGENDARY'];

function toDateInputValue(val) {
  if (!val) return '';
  const d = val.toDate ? val.toDate() : new Date(typeof val === 'number' ? val : String(val));
  return isNaN(d) ? '' : d.toISOString().split('T')[0];
}

const emptyLevel = (index) => ({
  level: index + 1,
  name: '',
  threshold: '',
  rarity: 'COMMON',
  icon: '',
  uploading: false,
});

class BadgeForm extends Component {
  constructor(props) {
    super(props);
    const init = props.initialData || {};
    this.state = {
      key:         init.key         || '',
      family:      init.family      || '',
      metric:      init.metric      || 'totalCheckins',
      metricLabel: init.metricLabel || '',
      description: init.description  || '',
      scope:       init.scope       || '',
      active:   init.active !== undefined ? init.active : true,
      validFrom: toDateInputValue(init.validFrom),
      validTo:   toDateInputValue(init.validTo),
      levels: init.levels && init.levels.length > 0
        ? init.levels.map((l, i) => ({
            level:     l.level     || i + 1,
            name:      l.name      || '',
            threshold: l.threshold !== undefined ? l.threshold : '',
            rarity:    l.rarity    || 'COMMON',
            icon:      l.icon      || '',
            uploading: false,
          }))
        : [emptyLevel(0)],
      saving: false,
      error:  '',
    };
    this.handleSave        = this.handleSave.bind(this);
    this.addLevel          = this.addLevel.bind(this);
    this.handleIconUpload  = this.handleIconUpload.bind(this);
  }

  handleChange(field, value) {
    // When family changes on a NEW badge, auto-fill key with the same value.
    // On edit, key is locked to whatever was saved — never change it.
    if (field === 'family' && !this.props.docId) {
      this.setState({ family: value, key: value });
    } else {
      this.setState({ [field]: value });
    }
  }

  handleLevelChange(index, field, value) {
    const levels = this.state.levels.map((l, i) => {
      if (i !== index) return l;
      return { ...l, [field]: value };
    });
    this.setState({ levels });
  }

  addLevel() {
    const { levels } = this.state;
    this.setState({ levels: [...levels, emptyLevel(levels.length)] });
  }

  removeLevel(index) {
    const levels = this.state.levels
      .filter((_, i) => i !== index)
      .map((l, i) => ({ ...l, level: i + 1 }));
    this.setState({ levels });
  }

  async handleIconUpload(index, file) {
    if (!file) return;
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      this.setState({ error: 'SVG files are not supported — please upload a PNG or JPEG instead.' });
      return;
    }
    const { family, key } = this.state;
    const folder = family.trim() || key.trim() || 'unknown';
    const level  = index + 1;
    const storageRef = firebase.storage().ref(`badgeIcons/${folder}/level${level}`);

    this.setState(prev => ({
      levels: prev.levels.map((l, i) => i === index ? { ...l, uploading: true } : l),
    }));

    try {
      const snapshot = await storageRef.put(file);
      const url = await snapshot.ref.getDownloadURL();
      this.setState(prev => ({
        levels: prev.levels.map((l, i) => i === index ? { ...l, icon: url, uploading: false } : l),
      }));
    } catch (err) {
      this.setState(prev => ({
        levels: prev.levels.map((l, i) => i === index ? { ...l, uploading: false } : l),
        error: `Icon upload failed: ${err.message}`,
      }));
    }
  }

  handleSave() {
    const { key, family, metric, metricLabel, description, scope, active, validFrom, validTo, levels } = this.state;
    const { docId, history } = this.props;

    if (!family) {
      this.setState({ error: 'Please select a badge family.' });
      return;
    }

    const parsedLevels = levels.map((l, i) => ({
      level:     i + 1,
      name:      l.name,
      threshold: Number(l.threshold),
      rarity:    l.rarity,
      icon:      l.icon || null,
    }));

    const data = {
      key:    key.trim(),
      family: family.trim(),
      metric,
      active,
      levels: parsedLevels,
    };
    if (metricLabel.trim()) data.metricLabel = metricLabel.trim();
    if (description.trim()) data.description = description.trim();
    if (scope.trim())       data.scope       = scope.trim();
    if (validFrom) data.validFrom = firebase.firestore.Timestamp.fromDate(new Date(validFrom));
    if (validTo)   data.validTo   = firebase.firestore.Timestamp.fromDate(new Date(validTo));

    this.setState({ saving: true, error: '' });

    const db = firebase.firestore();
    const promise = docId
      ? db.collection('badgeDefinitions').doc(docId).update(data)
      : db.collection('badgeDefinitions').doc(key.trim()).set(data);

    promise.then(() => {
      history.push('/badges');
    }).catch(err => {
      this.setState({ saving: false, error: err.message || 'Save failed. Please try again.' });
    });
  }

  render() {
    const { key, family, metric, metricLabel, description, scope, active, validFrom, validTo, levels, saving, error } = this.state;
    const { docId } = this.props;
    const isEdit = !!docId;

    const inputStyle = {
      width: '100%',
      padding: '8px 10px',
      border: '1px solid #ddd',
      borderRadius: 4,
      fontSize: 14,
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    };
    const labelStyle  = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#444' };
    const fieldStyle  = { marginBottom: 16 };

    return (
      <div>
        <Helmet><title>{isEdit ? 'Edit Badge' : 'New Badge'} — BarZone</title></Helmet>
        <div style={{ padding: 20, maxWidth: 700 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
            <button
              onClick={() => this.props.history.push('/badges')}
              style={{ background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}
            >
              Back
            </button>
            <h2 style={{ margin: 0 }}>{isEdit ? 'Edit Badge' : 'New Badge'}</h2>
          </div>

          {error && (
            <div style={{ background: '#fdecea', border: '1px solid #e74c3c', borderRadius: 4, padding: '10px 14px', marginBottom: 16, color: '#c0392b', fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Family dropdown — admin picks this; Badge Key auto-fills */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Badge Family</label>
            {isEdit ? (
              <input
                style={{ ...inputStyle, background: '#f5f5f5' }}
                value={FAMILY_OPTIONS.find(o => o.value === family)?.label || family}
                readOnly
              />
            ) : (
              <select
                style={inputStyle}
                value={family}
                onChange={e => this.handleChange('family', e.target.value)}
              >
                <option value="">— Select a family —</option>
                {FAMILY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* Badge Key — always read-only, auto-generated from family */}
          <div style={fieldStyle}>
            <label style={labelStyle}>
              Badge Key <span style={{ fontWeight: 400, color: '#888' }}>(auto-filled from family, cannot be changed)</span>
            </label>
            <input
              style={{ ...inputStyle, background: '#f5f5f5', color: '#888' }}
              value={key}
              readOnly
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Metric</label>
            <select style={inputStyle} value={metric} onChange={e => this.handleChange('metric', e.target.value)}>
              {METRIC_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Metric Label <span style={{ fontWeight: 400, color: '#888' }}>(shown in app, e.g. "cocktail bars")</span></label>
            <input
              style={inputStyle}
              value={metricLabel}
              onChange={e => this.handleChange('metricLabel', e.target.value)}
              placeholder="e.g. cocktail bars"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Description <span style={{ fontWeight: 400, color: '#888' }}>(shown on badge detail + profile title card)</span></label>
            <textarea
              style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              value={description}
              onChange={e => this.handleChange('description', e.target.value)}
              placeholder="e.g. Check in at cocktail bars around the world."
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Scope <span style={{ fontWeight: 400, color: '#888' }}>(optional — e.g. venue type filter)</span></label>
            <input
              style={inputStyle}
              value={scope}
              onChange={e => this.handleChange('scope', e.target.value)}
              placeholder="e.g. byCategoryUnique"
            />
          </div>

          <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="activeCheck"
              checked={active}
              onChange={e => this.handleChange('active', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="activeCheck" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }}>Active</label>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Valid From (optional)</label>
              <input
                type="date"
                style={inputStyle}
                value={validFrom}
                onChange={e => this.handleChange('validFrom', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Valid To (optional)</label>
              <input
                type="date"
                style={inputStyle}
                value={validTo}
                onChange={e => this.handleChange('validTo', e.target.value)}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>Levels</p>
              <button className="yellowBtn" style={{ padding: '4px 14px', fontSize: 13 }} onClick={this.addLevel}>+ Add Level</button>
            </div>

            {levels.map((level, i) => (
              <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 14, marginBottom: 10, background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Level {i + 1}</span>
                  {levels.length > 1 && (
                    <button
                      onClick={() => this.removeLevel(i)}
                      style={{ background: 'none', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 12 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={labelStyle}>Name</label>
                    <input
                      style={inputStyle}
                      value={level.name}
                      onChange={e => this.handleLevelChange(i, 'name', e.target.value)}
                      placeholder="e.g. Bronze Explorer"
                    />
                  </div>
                  <div style={{ flex: '1 1 100px' }}>
                    <label style={labelStyle}>Threshold</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={level.threshold}
                      min={0}
                      onChange={e => this.handleLevelChange(i, 'threshold', e.target.value)}
                      placeholder="e.g. 5"
                    />
                  </div>
                  <div style={{ flex: '1 1 130px' }}>
                    <label style={labelStyle}>Rarity</label>
                    <select style={inputStyle} value={level.rarity} onChange={e => this.handleLevelChange(i, 'rarity', e.target.value)}>
                      {RARITY_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: '2 1 200px' }}>
                    <label style={labelStyle}>Icon</label>
                    {level.icon ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <img src={level.icon} alt="icon" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #ddd' }} />
                        <button
                          type="button"
                          onClick={() => this.handleLevelChange(i, 'icon', '')}
                          style={{ fontSize: 11, padding: '2px 8px', background: 'none', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: 4, cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : null}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      style={{ fontSize: 13, fontFamily: 'inherit', width: '100%' }}
                      disabled={level.uploading}
                      onChange={e => this.handleIconUpload(i, e.target.files[0])}
                    />
                    {level.uploading && (
                      <span style={{ fontSize: 11, color: '#888', marginTop: 4, display: 'block' }}>Uploading…</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            className="yellowBtn"
            onClick={this.handleSave}
            disabled={saving}
            style={{ padding: '10px 28px', fontSize: 15, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving...' : 'Save Badge'}
          </button>
        </div>
      </div>
    );
  }
}

export default BadgeForm;
